import { LegendList } from "@-/com.npmjs.legendapp__list";
import { setStringAsync } from "@-/com.npmjs.expo-clipboard";
import { cn } from "@-/com.npmjs.cn";
import {
  Platform,
  Pressable,
  Text as RNText,
  View,
  type TextStyle,
} from "@-/com.npmjs.react-native";
import { useUniwind, withUniwind } from "@-/com.npmjs.uniwind";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  getRecentDebugLogsText,
  parseAnsiText,
  resolveAnsiColor,
  shareRecentDebugLogs,
  useRecentDebugLogs,
  type AnsiStyle,
  type DiagnosticsLogEntry,
  type DiagnosticsLogLevel,
  type RecentDebugLogEntry,
} from "@-/dev.improve.haniverse.lib.diagnostics";
import { useMemo, useState } from "react";
import { ToolbarMenuPageHeader } from "#toolbar-menu-header";

const DebugLogList = withUniwind(LegendList<RecentDebugLogEntry>);

// `font-mono` alone falls back to the proportional app font on native, so name a real face.
const MONO = "font-mono ios:font-[Menlo] android:font-[monospace]";

// Terminal palettes (GitHub light / dark dimmed) so logger colors stay legible on either theme.
const ANSI_PALETTES = {
  dark: {
    background: "#22272e",
    base: [
      "#768390",
      "#f47067",
      "#57ab5a",
      "#c69026",
      "#539bf5",
      "#b083f0",
      "#39c5cf",
      "#adbac7",
      "#909dab",
      "#ff938a",
      "#6bc46d",
      "#daaa3f",
      "#6cb6ff",
      "#dcbdfb",
      "#56d4dd",
      "#cdd9e5",
    ],
    dim: "#768390",
    foreground: "#adbac7",
  },
  light: {
    background: "#ffffff",
    base: [
      "#24292f",
      "#cf222e",
      "#116329",
      "#9a6700",
      "#0969da",
      "#8250df",
      "#1b7c83",
      "#6e7781",
      "#57606a",
      "#a40e26",
      "#1a7f37",
      "#7d4e00",
      "#218bff",
      "#a475f9",
      "#3192aa",
      "#8c959f",
    ],
    dim: "#6e7781",
    foreground: "#24292f",
  },
} as const;
type AnsiPalette = (typeof ANSI_PALETTES)[keyof typeof ANSI_PALETTES];

const LEVEL_CLASS_NAMES: Record<
  DiagnosticsLogLevel,
  { badge: string; badgeText: string; row: string }
> = {
  debug: { badge: "bg-muted", badgeText: "text-muted-foreground", row: "" },
  error: {
    badge: "bg-destructive/15",
    badgeText: "text-destructive",
    row: "border-l-destructive bg-destructive/5",
  },
  fatal: {
    badge: "bg-destructive",
    badgeText: "text-white",
    row: "border-l-destructive bg-destructive/10",
  },
  info: { badge: "bg-sky-500/15", badgeText: "text-sky-700 dark:text-sky-300", row: "" },
  trace: { badge: "bg-muted", badgeText: "text-muted-foreground", row: "" },
  warn: {
    badge: "bg-amber-500/15",
    badgeText: "text-amber-700 dark:text-amber-300",
    row: "border-l-amber-500 bg-amber-500/5",
  },
};

// The app logger prefixes each message with its call site, e.g. `[save-study-session.ts:7:12]`.
const CALL_SITE_PATTERN = /^\[([^\]\s]+:\d+(?::\d+)?)\]\s*/;

function formatClockTime(timestamp: number): string {
  const date = new Date(timestamp);
  const pad = (value: number, length = 2) => String(value).padStart(length, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`;
}

function ansiSpanStyle(style: AnsiStyle, palette: AnsiPalette): TextStyle | undefined {
  let foreground =
    style.foreground === null ? null : resolveAnsiColor(style.foreground, palette.base);
  let background =
    style.background === null ? null : resolveAnsiColor(style.background, palette.base);
  if (style.inverse) {
    [foreground, background] = [background ?? palette.background, foreground ?? palette.foreground];
  }
  if (style.dim) {
    // Nested native Text ignores `opacity`, so fade the color itself.
    foreground = foreground === null ? palette.dim : `${foreground}99`;
  }
  const decoration: TextStyle["textDecorationLine"] =
    style.underline && style.strikethrough
      ? "underline line-through"
      : style.underline
        ? "underline"
        : style.strikethrough
          ? "line-through"
          : undefined;
  const span: TextStyle = {
    ...(foreground === null ? {} : { color: foreground }),
    ...(background === null ? {} : { backgroundColor: background }),
    ...(style.bold ? { fontWeight: "700" } : {}),
    ...(style.italic ? { fontStyle: "italic" } : {}),
    ...(decoration === undefined ? {} : { textDecorationLine: decoration }),
  };
  return Object.keys(span).length === 0 ? undefined : span;
}

/** Renders terminal escape codes as styled spans; nested spans inherit the monospace face. */
function AnsiText({ className, text }: { className: string; text: string }) {
  const { theme } = useUniwind();
  const palette = ANSI_PALETTES[theme === "dark" ? "dark" : "light"];
  const segments = useMemo(() => parseAnsiText(text), [text]);
  return (
    <Text className={cn(MONO, className)} selectable>
      {segments.map((segment, index) => {
        // Colors come from the log itself at runtime, so they can't be static classNames.
        const style = ansiSpanStyle(segment.style, palette);
        return style === undefined ? (
          segment.text
        ) : (
          // oxlint-disable-next-line react/no-array-index-key -- segments are positional and immutable
          <RNText key={index} style={style}>
            {segment.text}
          </RNText>
        );
      })}
    </Text>
  );
}

function DebugLogRow({ item }: { item: DiagnosticsLogEntry }) {
  const callSite = CALL_SITE_PATTERN.exec(item.message);
  const message = callSite === null ? item.message : item.message.slice(callSite[0].length);
  const level = LEVEL_CLASS_NAMES[item.level];
  return (
    <View
      className={cn(
        "gap-1 border-b border-l-2 border-b-border border-l-transparent px-3 py-2",
        level.row,
      )}
    >
      <View className="flex-row items-center gap-2">
        <Text className={cn(MONO, "text-[10px] leading-4 text-muted-foreground")}>
          {formatClockTime(item.timestamp)}
        </Text>
        <View className={cn("rounded-xs px-1", level.badge)}>
          <Text className={cn(MONO, "text-[9px] leading-4 font-bold", level.badgeText)}>
            {item.level.toUpperCase()}
          </Text>
        </View>
        <Text
          className={cn(MONO, "flex-1 text-right text-[10px] leading-4 text-muted-foreground")}
          ellipsizeMode="head"
          numberOfLines={1}
        >
          {callSite?.[1] ?? item.source}
        </Text>
      </View>
      <AnsiText className="text-[11px] leading-4 text-foreground" text={message} />
      {item.error === null ? null : (
        <Text className={cn(MONO, "text-[10px] leading-4 text-destructive")} selectable>
          {item.error}
        </Text>
      )}
    </View>
  );
}

function DebugPanelAction({
  disabled,
  label,
  onPress,
  title,
}: {
  disabled: boolean;
  label: string;
  onPress: () => void;
  title: string;
}) {
  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      className="rounded-full bg-muted px-3.5 py-1.5 active:opacity-70 disabled:opacity-40"
      disabled={disabled}
      onPress={onPress}
    >
      <Text className="text-sm font-medium">{title}</Text>
    </Pressable>
  );
}

/**
 * The Debug panel's body on the menu surface that `ToolbarDebugGesture` grows out of the button:
 * live logs, with Copy and Share in its header.
 */
export function ToolbarDebugPanel({ close }: { close: () => void }) {
  const logs = useRecentDebugLogs();
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const exportLogs = async (action: "copy" | "share") => {
    setBusy(true);
    setStatus(null);
    try {
      const text = getRecentDebugLogsText();
      if (action === "copy") {
        await setStringAsync(text);
        setStatus("Logs copied");
      } else {
        await shareRecentDebugLogs(text);
      }
    } catch {
      setStatus(
        action === "copy"
          ? "Could not copy logs. Please try again."
          : "Could not share logs. Try copying them instead.",
      );
    } finally {
      setBusy(false);
    }
  };
  const exportDisabled = busy || logs.length === 0;
  return (
    <>
      <ToolbarMenuPageHeader close={close} closeLabel="Close debug panel" title="Debug">
        <DebugPanelAction
          disabled={exportDisabled}
          label="Copy logs"
          onPress={() => void exportLogs("copy")}
          title="Copy"
        />
        <DebugPanelAction
          disabled={exportDisabled}
          label={Platform.OS === "web" ? "Download logs" : "Share logs"}
          onPress={() => void exportLogs("share")}
          title={Platform.OS === "web" ? "Download" : "Share"}
        />
      </ToolbarMenuPageHeader>
      <View className="shrink-0 flex-row items-center gap-1.5 px-3 pb-2">
        <View className="size-1.5 rounded-full bg-green-500" />
        <Text className="min-w-0 flex-1 text-xs text-muted-foreground" numberOfLines={1}>
          {`Live logs · Latest ${logs.length} of up to 500`}
        </Text>
        {status === null ? null : (
          <Text
            accessibilityLiveRegion="polite"
            className="shrink-0 text-xs text-muted-foreground"
            numberOfLines={1}
          >
            {status}
          </Text>
        )}
      </View>
      <View className="h-px shrink-0 bg-border" />
      <DebugLogList
        className="flex-1"
        contentContainerClassName="pb-4"
        data={logs}
        // Every append re-runs LegendList's scroll-to-end bootstrap with an 8-frame budget.
        // Its 100pt default overshoots these rows enough to abort in dev, and the abort warning
        // is itself captured as a log, which appends again and loops.
        estimatedItemSize={64}
        initialScrollAtEnd
        keyExtractor={(item) => String(item.id)}
        ListEmptyComponent={
          <Text className="px-4 py-8 text-center text-muted-foreground">
            Waiting for logs… New logs will appear here automatically.
          </Text>
        }
        maintainScrollAtEnd
        maintainVisibleContentPosition
        recycleItems
        renderItem={({ item }) => <DebugLogRow item={item} />}
        scrollEventThrottle={100}
        testID="debug-panel-logs"
      />
    </>
  );
}
